/**
 * AuthContext
 *
 * Authentication via OAuth 2.0 Authorization Code Grant + PKCE.
 * Targets Drupal Simple OAuth module. No username/password ever touches the
 * frontend — Drupal's own login form handles credentials.
 *
 * Flow:
 *   1. login(from)  — generates code_verifier + code_challenge, stores verifier
 *                     in sessionStorage, redirects browser to:
 *                       /oauth/authorize?response_type=code&client_id=…
 *                         &redirect_uri=…&scope=…
 *                         &code_challenge=…&code_challenge_method=S256
 *
 *   2. Drupal authenticates the user and redirects to:
 *        {VITE_OAUTH_REDIRECT_URI}?code={authorization_code}
 *
 *   3. AuthCallback component calls completeLogin(code):
 *        POST /oauth/token  body: grant_type=authorization_code
 *                                 code=…  code_verifier=…
 *                                 client_id=…  redirect_uri=…
 *        → { access_token, token_type, expires_in, refresh_token }
 *        Token saved to localStorage["token"] (Bearer interceptor picks it up).
 *        Profile fetched from GET /jsonapi/user/user?filter[drupal_internal__uid]={uid}&include=roles
 *        (Bearer token) where uid comes from the JWT uid claim (numeric).
 *
 *   4. Session resume — on mount, if localStorage["token"] is present:
 *        Valid token  → fetch profile, set user.
 *        Expired token → attempt refresh_token grant, then fetch profile.
 *        Refresh fails → clear token, treat as unauthenticated.
 *
 *   5. logout() — clears localStorage and resets state.
 *
 *   Force logout → Axios 401 fires "drupal:auth:logout" event → same cleanup.
 *
 * Required env vars:
 *   VITE_OAUTH_CLIENT_ID      — Simple OAuth consumer UUID
 *   VITE_OAUTH_CLIENT_SECRET  — consumer secret (omit for public clients)
 *   VITE_OAUTH_SCOPE          — e.g. "authenticated"
 *   VITE_OAUTH_REDIRECT_URI   — e.g. "http://localhost:3000/auth/callback"
 *   VITE_DRUPAL_DIRECT_URL    — full Drupal URL (dev + prod); used for OAuth
 *                               browser redirects and token fetch calls
 *   VITE_DRUPAL_BASE_URL      — empty in dev (Axios uses Vite proxy); full URL in prod
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import apiClient from '@/lib/axios';
import { ROLES } from '@/lib/constants';
import { parseJwtPayload, isTokenExpired } from '@/lib/utils';
import { queryClient } from '@/lib/queryClient';
import { useMapStore } from '@/stores/mapStore';
import { useUIStore } from '@/stores/uiStore';

// ─── localStorage keys ─────────────────────────────────────────────────────────
const LS_TOKEN = 'token';        // OAuth token object
const LS_USER  = 'drupal_user';  // cached user profile { id, name, role }

// ─── sessionStorage keys ───────────────────────────────────────────────────────
const SK_VERIFIER = 'pkce_code_verifier';
const SK_STATE    = 'pkce_state';
const SK_REDIRECT = 'auth_redirect_target';

// DRUPAL_DIRECT — full URL used only for window.location.href redirects to
// Drupal's /oauth/authorize. The browser must land on the Drupal domain so
// Drupal can read/write its own session cookie during login.
const DRUPAL_DIRECT = (
  import.meta.env.VITE_DRUPAL_DIRECT_URL ||
  import.meta.env.VITE_DRUPAL_BASE_URL   ||
  ''
).replace(/\/?$/, '');

// TOKEN_BASE — base URL for fetch() calls to /oauth/token.
// In dev this is empty so the call goes through the Vite proxy (/oauth is
// proxied to wtsm.ddev.site), avoiding the CORS preflight that Drupal blocks.
// In prod VITE_DRUPAL_BASE_URL is the full origin and is used directly.
const TOKEN_BASE = (import.meta.env.VITE_DRUPAL_BASE_URL || '').replace(/\/?$/, '');

// ─── PKCE helpers ──────────────────────────────────────────────────────────────
function randomBase64Url(byteLength = 32) {
  const array = crypto.getRandomValues(new Uint8Array(byteLength));
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function generatePkce() {
  const verifier = randomBase64Url(32);

  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  );
  const challenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

  // state is an opaque random value used to prevent CSRF on the callback
  const state = randomBase64Url(16);

  return { verifier, challenge, state };
}

// ─── Role mapping ──────────────────────────────────────────────────────────────
function drupalRoleToFrontend(drupalRoles = []) {
  if (drupalRoles.includes('administrator')) return ROLES.ADMIN;
  if (drupalRoles.includes('site_owner'))    return ROLES.SITE_OWNER;
  return ROLES.CONTRIBUTOR;
}

// ─── Token storage ─────────────────────────────────────────────────────────────
function saveToken(tokenData) {
  const now = Math.floor(Date.now() / 1000);
  const stored = {
    ...tokenData,
    date: now,
    expirationDate: now + (tokenData.expires_in ?? 300),
  };
  localStorage.setItem(LS_TOKEN, JSON.stringify(stored));
  return stored;
}

function loadToken() {
  try {
    const raw = localStorage.getItem(LS_TOKEN);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearToken() {
  localStorage.removeItem(LS_TOKEN);
  localStorage.removeItem(LS_USER);
  localStorage.removeItem('oauthSettings'); // clean up library remnant if present
}

// ─── User profile cache ────────────────────────────────────────────────────────
function saveUserProfile(profile) {
  localStorage.setItem(LS_USER, JSON.stringify(profile));
}

function loadUserProfile() {
  try {
    const raw = localStorage.getItem(LS_USER);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// ─── Token refresh ─────────────────────────────────────────────────────────────
async function refreshToken(token) {
  const formData = new URLSearchParams();
  formData.append('grant_type',    'refresh_token');
  formData.append('client_id',     import.meta.env.VITE_OAUTH_CLIENT_ID);
  formData.append('refresh_token', token.refresh_token);
  formData.append('scope',         import.meta.env.VITE_OAUTH_SCOPE || 'authenticated');
  if (import.meta.env.VITE_OAUTH_CLIENT_SECRET) {
    formData.append('client_secret', import.meta.env.VITE_OAUTH_CLIENT_SECRET);
  }

  // Use TOKEN_BASE (empty in dev → Vite proxy) to avoid CORS on the POST
  const res  = await fetch(`${TOKEN_BASE}/oauth/token`, {
    method:  'POST',
    headers: { Accept: 'application/json' },
    body:    formData,
  });
  const data = await res.json();
  if (!res.ok || !data.access_token) {
    throw new Error(data.error_description || 'Token refresh failed');
  }
  return data;
}

// ─── User profile ──────────────────────────────────────────────────────────────
// Uses native fetch (not apiClient) so the Axios 401 interceptor never fires
// during profile resolution — a failed profile call must not wipe the session.
//
// Drupal Simple OAuth puts the user UUID in the `sub` claim. We prefer the
// direct resource path /jsonapi/user/user/{uuid}?include=roles. If sub is not
// a UUID (some OAuth configs emit a numeric string), we fall back to a filter
// query on drupal_internal__uid using the `uid` claim.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

async function fetchUserProfile(accessToken) {
  const payload = parseJwtPayload(accessToken);
  const sub     = String(payload?.sub ?? '');
  const uid     = payload?.uid ?? null;
  const jwtFallback = {
    id:        null,
    name:      payload?.name ?? payload?.preferred_username ?? 'User',
    email:     null,
    role:      drupalRoleToFrontend(payload?.roles ?? []),
    avatarUrl: null,
  };

  if (!sub && !uid) return jwtFallback;

  try {
    const url = UUID_RE.test(sub)
      ? `${TOKEN_BASE}/jsonapi/user/user/${sub}?include=roles`
      : `${TOKEN_BASE}/jsonapi/user/user?filter[drupal_internal__uid]=${uid ?? sub}&include=roles`;

    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/vnd.api+json',
      },
    });
    if (!res.ok) return jwtFallback;
    const json = await res.json();

    // Direct-resource responses have data as an object; collection responses use an array.
    const userData = Array.isArray(json.data) ? json.data[0] : json.data;
    if (!userData) return jwtFallback;

    const id       = userData.attributes?.drupal_internal__uid ?? null;
    const name     = userData.attributes?.name                 ?? jwtFallback.name;
    const roleIds  = (json.included ?? [])
      .filter((i) => i.type === 'user_role--user_role')
      .map((i) => i.attributes?.drupal_internal__id)
      .filter(Boolean);
    const avatarUrl = userData.attributes?.field_user_avatar?.uri?.url ?? null;

    return {
      id,
      name,
      email: userData.attributes?.mail ?? null,
      role: roleIds.length ? drupalRoleToFrontend(roleIds) : jwtFallback.role,
      avatarUrl,
    };
  } catch {
    return jwtFallback;
  }
}

// ─── Context ───────────────────────────────────────────────────────────────────
export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,            setUser]            = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading,       setIsLoading]       = useState(
    () => loadToken() !== null,
  );

  // ── Force-logout (Axios 401 interceptor fires this event) ──────────────────
  useEffect(() => {
    const handle = () => {
      clearToken();
      queryClient.clear();
      useMapStore.getState().reset();
      useUIStore.getState().reset();
      window.dispatchEvent(new CustomEvent('impact:map:clear'));
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    };
    window.addEventListener('drupal:auth:logout', handle);
    return () => window.removeEventListener('drupal:auth:logout', handle);
  }, []);

  // ── Session resume on mount ─────────────────────────────────────────────────
  useEffect(() => {
    const resume = async () => {
      const token = loadToken();
      if (!token) { setIsLoading(false); return; }

      let active       = token;
      let tokenRefreshed = false;

      if (isTokenExpired(token.access_token)) {
        try {
          active        = saveToken(await refreshToken(token));
          tokenRefreshed = true;
        } catch {
          // Refresh token is also expired or revoked — force re-login.
          clearToken();
          setIsLoading(false);
          return;
        }
      }

      // Restore profile from cache so there's no API round-trip on every refresh.
      // Re-fetch when: token was just refreshed, cache is missing, name is blank,
      // or id is null (indicates a prior fetch fell back to JWT defaults and failed).
      // A profile is considered complete when it has all fields fetchUserProfile provides.
      // Missing email means the cached profile predates that field — re-fetch.
      const PROFILE_FIELDS = ['id', 'name', 'email', 'role', 'avatarUrl'];
      let profile = tokenRefreshed ? null : loadUserProfile();
      if (!profile || PROFILE_FIELDS.some((k) => !(k in profile))) profile = null;
      if (!profile) {
        profile = await fetchUserProfile(active.access_token);
        if (profile) saveUserProfile(profile);
      }

      // fetchUserProfile only returns null when the JWT has no sub claim.
      // Guard anyway to ensure user is never null while isAuthenticated is true.
      if (!profile) {
        const payload = parseJwtPayload(active.access_token);
        profile = {
          id:       null,
          name:     payload?.name ?? 'User',
          email:    null,
          role:     drupalRoleToFrontend(payload?.roles ?? []),
          avatarUrl: null,
        };
      }

      setUser(profile);
      setIsAuthenticated(true);
      setIsLoading(false);
    };

    resume();
  }, []);

  // ── Login — initiates PKCE redirect ────────────────────────────────────────
  // `from` is the path to return to after a successful callback.
  const login = useCallback(async (from = '/') => {
    sessionStorage.setItem(SK_REDIRECT, from);

    const { verifier, challenge, state } = await generatePkce();
    sessionStorage.setItem(SK_VERIFIER, verifier);
    sessionStorage.setItem(SK_STATE,    state);

    // Navigate directly to Drupal (not via Vite proxy) so Drupal can set its
    // own session cookie on the wtsm.ddev.site domain during the login step.
    const params = new URLSearchParams({
      response_type:         'code',
      client_id:             import.meta.env.VITE_OAUTH_CLIENT_ID,
      redirect_uri:          import.meta.env.VITE_OAUTH_REDIRECT_URI,
      scope:                 import.meta.env.VITE_OAUTH_SCOPE || 'authenticated',
      code_challenge:        challenge,
      code_challenge_method: 'S256',
      state,
    });

    window.location.href = `${DRUPAL_DIRECT}/oauth/authorize?${params}`;
  }, []);

  // ── Complete login — called by AuthCallback after Drupal redirects back ─────
  // Returns the path the user originally tried to reach.
  const completeLogin = useCallback(async (code, returnedState) => {
    // Verify state to prevent CSRF
    const expectedState = sessionStorage.getItem(SK_STATE);
    sessionStorage.removeItem(SK_STATE);
    if (!expectedState || expectedState !== returnedState) {
      throw new Error('Invalid OAuth state — possible CSRF attack. Please try signing in again.');
    }

    const verifier = sessionStorage.getItem(SK_VERIFIER);
    sessionStorage.removeItem(SK_VERIFIER);
    if (!verifier) throw new Error('PKCE verifier missing — please try signing in again.');

    const formData = new URLSearchParams();
    formData.append('grant_type',    'authorization_code');
    formData.append('client_id',     import.meta.env.VITE_OAUTH_CLIENT_ID);
    formData.append('redirect_uri',  import.meta.env.VITE_OAUTH_REDIRECT_URI);
    formData.append('code',          code);
    formData.append('code_verifier', verifier);
    if (import.meta.env.VITE_OAUTH_CLIENT_SECRET) {
      formData.append('client_secret', import.meta.env.VITE_OAUTH_CLIENT_SECRET);
    }

    // Use TOKEN_BASE (empty in dev → Vite proxy) to avoid CORS on the POST
    const res  = await fetch(`${TOKEN_BASE}/oauth/token`, {
      method:  'POST',
      headers: { Accept: 'application/json' },
      body:    formData,
    });
    const tokenData = await res.json();
    if (!res.ok || !tokenData.access_token) {
      throw new Error(tokenData.error_description || 'Token exchange failed');
    }

    const stored  = saveToken(tokenData);
    let profile = await fetchUserProfile(stored.access_token);
    // fetchUserProfile only returns null when there's no UUID in the JWT.
    // Guard anyway so isAuthenticated is never true with user === null.
    if (!profile) {
      const p = parseJwtPayload(stored.access_token);
      profile = { id: null, name: p?.name ?? 'User', email: null, role: drupalRoleToFrontend(p?.roles ?? []), avatarUrl: null };
    }
    saveUserProfile(profile);
    setUser(profile);
    setIsAuthenticated(true);

    const redirectTo = sessionStorage.getItem(SK_REDIRECT) || '/';
    sessionStorage.removeItem(SK_REDIRECT);
    return redirectTo;
  }, []);

  // ── Logout ──────────────────────────────────────────────────────────────────
  const logout = useCallback(() => {
    clearToken();
    queryClient.clear();
    useMapStore.getState().reset();
    useUIStore.getState().reset();
    window.dispatchEvent(new CustomEvent('impact:map:clear'));
    setUser(null);
    setIsAuthenticated(false);
    setIsLoading(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated, isLoading, login, completeLogin, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
